import React, { useEffect, useState } from "react";
import Pagination from "components/Pagination/Pagination.js";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@mui/material/Typography";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import ProfileCard from "./ProfileCard";

import { useSearch } from "context/SearchContext";
import { useAuth } from "context/AuthContext";
import { useWebSocket } from "context/WebSocketContext";
import ProfilesApi from "api_calls/ProfilesApi";

import { registerForPush } from "pushNotifications";
import FcmAPI from "api_calls/FcmAPI";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
const useStyles = makeStyles(styles);

export default function Dashboard() {
  const classes = useStyles();

  // -----------------------------
  // DATA STATE
  // -----------------------------
  const [profiles, setProfiles] = useState([]);
  const [likedIds, setLikedIds] = useState([]);
  const [blockedIds, setBlockedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { searchQuery } = useSearch();
  const { user } = useAuth();
  const { registerHandler } = useWebSocket();

  // -----------------------------
  // 🔔 NOTIFICATION PERMISSION (UI ONLY)
  // -----------------------------
  const [permission, setPermission] = useState(Notification.permission);

  const enableNotifications = async () => {
    const token = await registerForPush();

    if (token) {
      await FcmAPI.updateFcmToken(token);
      setPermission("granted");
      console.log("✅ Push enabled & synced");
    }
  };

  // -----------------------------
  // FETCH PROFILES
  // -----------------------------
  useEffect(() => {
    const fetchProfiles = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const response = await ProfilesApi.fetchProfiles(
          user.gender || "MALE",
          user.user_id || "",
          currentPage,
          searchQuery
        );

        if (response) {
          setProfiles(response.circles || []);
          setLikedIds(
            Array.isArray(response.liked_ids) ? response.liked_ids : []
          );
          setBlockedIds(
            Array.isArray(response.blocked_ids) ? response.blocked_ids : []
          );
          setTotalPages(response.total_pages || 1);
        } else {
          setProfiles([]);
          setLikedIds([]);
          setBlockedIds([]);
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        setProfiles([]);
        setLikedIds([]);
        setBlockedIds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [user, currentPage, searchQuery]);

  // -----------------------------
  // PAGINATION
  // -----------------------------
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // -----------------------------
  // BLOCK / LIKE
  // -----------------------------
  const handleBlockSuccess = (blockedUserId) => {
    setProfiles((prev) =>
      prev.filter((profile) => profile.user_id !== blockedUserId)
    );
    setBlockedIds((prev) => [...prev, blockedUserId]);
  };

  const handleLikeSuccess = (likedUserId) => {
    setLikedIds((prev) =>
      prev.includes(likedUserId) ? prev : [...prev, likedUserId]
    );
  };

  // -----------------------------
  // WEBSOCKET EVENTS
  // -----------------------------
  useEffect(() => {
    const handleUserBlocked = (data) => {
      if (
        data?.status === "success" &&
        data?.result?.data?.message === "block"
      ) {
        handleBlockSuccess(data.peer_id);
      }
    };

    registerHandler("block", handleUserBlocked);
    return () => registerHandler("block", null);
  }, [registerHandler]);

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <>
      <Typography
        sx={{
          fontWeight: "normal",
          fontSize: "15px",
          fontFamily: "Calibri, sans-serif",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        💕 Discover someone special today
      </Typography>

      {/* 🔔 Notification permission UI */}
      <div style={{ marginTop: "6px", marginBottom: "10px" }}>
        {permission === "granted" && (
          <span style={{ fontSize: "13px", color: "#0f766e" }}>
            🔔 Call notifications enabled
          </span>
        )}

        {permission === "default" && (
          <button
            onClick={enableNotifications}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              cursor: "pointer",
              fontSize: "13px",
              background: "#f3f3f3",
            }}
          >
            🔔 Enable call notifications
          </button>
        )}

        {permission === "denied" && (
          <span style={{ color: "red", fontSize: "13px" }}>
            Notifications are blocked in your browser settings.
          </span>
        )}
      </div>

      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
        >
          <CircularProgress color="primary" />
        </div>
      ) : (
        <GridContainer>
          {profiles.length > 0 ? (
            profiles.map((profile) => (
              <GridItem key={profile.user_id} xs={12} sm={12} md={3}>
                <ProfileCard
                  profile={profile}
                  onBlockSuccess={handleBlockSuccess}
                  onLikeSuccess={handleLikeSuccess}
                  initialLiked={likedIds.includes(profile.user_id)}
                  initialBlocked={blockedIds.includes(profile.user_id)}
                  context="dashboard"
                />
              </GridItem>
            ))
          ) : (
            <GridItem xs={12}>
              <h4>No profiles found</h4>
            </GridItem>
          )}
        </GridContainer>
      )}

      <GridContainer justify="center">
        <Pagination
          pages={[
            {
              text: "PREV",
              onClick: () => handlePageChange(currentPage - 1),
              disabled: currentPage === 1,
            },
            ...Array.from({ length: totalPages }, (_, i) => ({
              text: i + 1,
              active: i + 1 === currentPage,
              onClick: () => handlePageChange(i + 1),
            })),
            {
              text: "NEXT",
              onClick: () => handlePageChange(currentPage + 1),
              disabled: currentPage === totalPages,
            },
          ]}
        />
      </GridContainer>
    </>
  );
}
